/**
 * Console Error Capture Utility
 *
 * Intercepts and stores console.error calls for debugging purposes.
 * Maintains a rolling buffer of errors with timestamps and stack traces.
 *
 * Features:
 * - Captures console.error calls without interfering with normal logging
 * - Stores up to 100 most recent errors (prevents memory issues)
 * - Includes timestamp and stack trace for each error
 * - Provides formatted error log for clipboard export
 * - Thread-safe error storage
 *
 * Usage:
 * ```typescript
 * // Initialize early in app lifecycle (main.tsx or App.tsx)
 * initConsoleErrorCapture();
 *
 * // Get formatted error log
 * const log = getFormattedErrorLog();
 *
 * // Clear error history
 * clearErrorLog();
 *
 * // Get error count
 * const count = getErrorCount();
 * ```
 */

interface CapturedError {
  timestamp: number;
  message: string;
  stack?: string;
  args: unknown[];
}

// Rolling buffer of errors (max 100 entries)
const MAX_ERRORS = 100;
let errorLog: CapturedError[] = [];
let originalConsoleError: typeof console.error;

/**
 * Initialize console error capture
 * Call this early in app lifecycle (main.tsx or App.tsx)
 */
export function initConsoleErrorCapture(): void {
  if (typeof window === 'undefined') {
    return; // Skip on server-side
  }

  // Store original console.error
  originalConsoleError = console.error;

  // Override console.error
  console.error = (...args: unknown[]) => {
    // Call original console.error first (don't interfere with normal logging)
    originalConsoleError.apply(console, args);

    // Capture error
    try {
      const error: CapturedError = {
        timestamp: Date.now(),
        message: args.map(arg => formatArg(arg)).join(' '),
        stack: captureStackTrace(),
        args: args,
      };

      // Add to rolling buffer
      errorLog.push(error);

      // Trim to max size
      if (errorLog.length > MAX_ERRORS) {
        errorLog = errorLog.slice(-MAX_ERRORS);
      }
    } catch (err) {
      // Silently fail to avoid infinite loops
      originalConsoleError('[consoleErrorCapture] Failed to capture error:', err);
    }
  };

  console.log('[consoleErrorCapture] Console error capture initialized');
}

/**
 * Format argument for display
 */
function formatArg(arg: unknown): string {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}\n${arg.stack || ''}`;
  }
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg, null, 2);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
}

/**
 * Capture stack trace at error location
 */
function captureStackTrace(): string {
  try {
    const stack = new Error().stack || '';
    // Remove first 3 lines (Error, captureStackTrace, console.error override)
    const lines = stack.split('\n').slice(3);
    return lines.join('\n');
  } catch {
    return '';
  }
}

/**
 * Get formatted error log for clipboard export
 *
 * Format:
 * ```
 * === Development Error Log ===
 * Generated: 2025-12-17 10:30:45
 * Total Errors: 5
 *
 * [1] 2025-12-17 10:25:12
 * Error message here
 * Stack trace:
 *   at Component (file:line:col)
 *   at Parent (file:line:col)
 * ---
 * ```
 */
export function getFormattedErrorLog(): string {
  if (errorLog.length === 0) {
    return '=== Development Error Log ===\nNo errors captured.';
  }

  const now = new Date();
  const header = [
    '=== Development Error Log ===',
    `Generated: ${formatTimestamp(now.getTime())}`,
    `Total Errors: ${errorLog.length}`,
    `Browser: ${navigator.userAgent}`,
    '',
  ].join('\n');

  const entries = errorLog.map((error, index) => {
    const timestamp = formatTimestamp(error.timestamp);
    const stack = error.stack ? `\nStack trace:\n${error.stack}` : '';
    return `[${index + 1}] ${timestamp}\n${error.message}${stack}\n---`;
  }).join('\n\n');

  return `${header}\n${entries}`;
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Get error count
 */
export function getErrorCount(): number {
  return errorLog.length;
}

/**
 * Clear error log
 */
export function clearErrorLog(): void {
  errorLog = [];
  console.log('[consoleErrorCapture] Error log cleared');
}

/**
 * Get raw error log (for advanced use cases)
 */
export function getRawErrorLog(): readonly CapturedError[] {
  return Object.freeze([...errorLog]);
}
